import { useCallback } from 'react';
import { useMonitoring } from 'lib/hooks/useMonitoring';
import useStrapiOrderContext from 'lib/hooks/useStrapiOrderContext';
import { gtmFireEvent } from 'components/orders/utilities/gtmDataLayerUtils';
import { cleanPropertiesForGTM } from 'components/orders/utilities/cleanPropertiesForGTM';
import { waitForAuthOptionsFromDom } from 'lib/analytics/mixpanel/authOptions';

type OrderPageIdentifiers =
  | 'Order Page'
  | 'Checkout Page'
  | 'Confirmation Page';

interface JoinFlowPageViewProps {
  pageIdentifier: OrderPageIdentifiers;
  additionalProperties?: { [key: string]: string | string[] };
}

export const useJoinFlowPageView = ({
  pageIdentifier,
  additionalProperties,
}: JoinFlowPageViewProps) => {
  const {
    strapiData,
    dataLayer: { user },
    orderParams,
  } = useStrapiOrderContext();

  const internal_tracking_name = strapiData?.attributes?.internal_tracking_name;
  const { log } = useMonitoring();

  const onPageLoad = useCallback(async () => {
    log('INFO', `${pageIdentifier} Loaded`);
    const authOptions =
      pageIdentifier === 'Order Page'
        ? await waitForAuthOptionsFromDom()
        : undefined;
    const {
      id,
      email,
      firstName,
      lastName,
      role,
      segment,
      package_name,
      idDigest,
      external_id,
    } = user;
    // TODO: enforce type constraints so that this uses typescript
    //...to alert if properties are changing
    const properties = {
      ...orderParams,
      id,
      Email: email || orderParams.savedEmail,
      UserFirstName: firstName,
      UserLastName: lastName,
      Role: role,
      UserSegment: segment,
      package_name,
      Package: package_name,
      idDigest,
      ExternalId: external_id,
      auth_method: user.authMethod,
      account_auth_type: user.accountAuthType,
      OfferName: internal_tracking_name,
      SiteKey: 'atk',
      Environment: 'standard',
      IdDigest: '',
      ...additionalProperties,
      ...(authOptions && { auth_options: authOptions }),
    };

    const gtmProperties = cleanPropertiesForGTM(properties);

    gtmFireEvent({
      eventName: 'pageDataLoaded',
      properties: gtmProperties,
    });
  }, [
    orderParams,
    user,
    internal_tracking_name,
    log,
    additionalProperties,
    pageIdentifier,
  ]);

  return { onPageLoad };
};
